import { TransformLearningProgressService } from '../services/TransformLearningProgressService'
import { useTransformLearningStore } from '../stores/TransformLearningStore'
import { useTransformOwnerContext } from './useTransformOwnerContext'
import { handleErrors } from '../utils/errorHandler'

// Local mirror of the reset the server applies on restart, so the UI (badges, progress
// bar, continue card) reflects the cleared state immediately without a reload.
const RESET_PATCH = Object.freeze({
  completed: false,
  scorePercent: null,
  correctCount: null,
  totalCount: null,
  passed: false,
  percentComplete: 0,
  draftAnswers: {},
  currentQuestionIndex: 0,
})

/**
 * Restart (retake) a quiz lesson. Calls the gateway to reset the persisted progress and
 * mirrors the reset into the store. Shared by the lesson card and the continue card,
 * which previously duplicated this call + patch and swallowed errors silently.
 *
 * @returns {{ restartQuiz: (args: { lessonId: string, courseId: string }) => Promise<boolean> }}
 */
export function useQuizRestart() {
  const store = useTransformLearningStore()
  const progressService = TransformLearningProgressService()

  async function restartQuiz({ lessonId, courseId }) {
    // Read-only in the admin view-as context: the reset would clear the ADMIN's own
    // persisted progress (uid comes from the token), not the viewed user's.
    if (useTransformOwnerContext().isActive.value) return false
    try {
      const ok = await progressService.restartQuiz({ lessonId, courseId })
      if (ok) store.applyLessonProgress(lessonId, { ...RESET_PATCH })
      return ok
    } catch (err) {
      handleErrors('useQuizRestart.restartQuiz', err, { sendToSentry: false })
      return false
    }
  }

  return { restartQuiz }
}
